import { useMsal } from '@azure/msal-react';
import { msalInstance } from './msalConfig';
import type { AuthAccount } from './useMockAuth';

const TOKEN_SCOPES = ['openid', 'profile', 'email'];

/**
 * Get the current MSAL ID token directly from the instance cache.
 * Used by apiClient interceptor — does not depend on hook render timing.
 */
export async function getMsalToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: TOKEN_SCOPES,
      account: accounts[0],
    });
    return result.idToken;
  } catch {
    return null;
  }
}

export function useRealAuth() {
  const { instance, accounts } = useMsal();
  const msalAccount = accounts[0] ?? null;

  const login = async () => {
    await instance.loginRedirect({ scopes: TOKEN_SCOPES });
  };

  const logout = async () => {
    await instance.logoutRedirect();
  };

  const account: AuthAccount | null = msalAccount
    ? {
        oid: (msalAccount.idTokenClaims as { oid?: string })?.oid ?? msalAccount.localAccountId,
        email: msalAccount.username,
        name: msalAccount.name ?? msalAccount.username,
        tenantId: msalAccount.tenantId,
        systemRole: 'USER',
        allowedUserId: '',
      }
    : null;

  return {
    isAuthenticated: !!msalAccount,
    isLoading: false,
    account,
    login,
    logout,
    getAccessToken: getMsalToken,
  };
}
